import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';

import type { SelectedForDeletion } from 'webapp/components/board/popup-delete-confirm';
import FormCheckboxComponent from 'webapp/components/form/checkbox';

interface Args {
	boardId: string;
	onChangeSelectedItems: (selectedItems: SelectedForDeletion) => void;
	onRecenter: () => void;
}

export default class BoardUnusedMetricsComponent extends Component<Args> {
	@service declare logger: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;

	@tracked customQueries: number[] = [];
	@tracked scripts: number[] = [];
	@tracked deleteCustomQueries = false;
	@tracked deleteScripts = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.getUnusedCustomQueriesAndScripts();
	}

	get customQueriesCount(): number {
		return this.customQueries.length;
	}

	get scriptsCount(): number {
		return this.scripts.length;
	}

	get showContent(): boolean {
		return this.customQueriesCount > 0 || this.scriptsCount > 0;
	}

	get customQueriesLabel(): string {
		const customQueriesCount = this.customQueriesCount;
		return customQueriesCount ? `Delete unused Built metrics (${customQueriesCount})` : '';
	}

	get scriptsLabel(): string {
		const scriptsCount = this.scriptsCount;
		return scriptsCount ? `Delete unused Calculated metrics (${scriptsCount})` : '';
	}

	getUnusedCustomQueriesAndScripts(): void {
		this.requestService
			.api<APIData.Metrics.UnusedResponse>(
				'GET',
				`/metrics/unused?boardId=${this.args.boardId}`,
				null,
				{
					skipShowingErrorInfolayer: true,
				},
			)
			.then((reply) => {
				// reply = JSON.parse('{"customQueries":[13300],"scripts":[855031]}');
				this.customQueries = reply.customQueries;
				this.scripts = reply.scripts;
				this.args.onRecenter();
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	updateSelectedItems(): void {
		const selectedItems: SelectedForDeletion = {
			customQueries: [],
			scripts: [],
		};

		selectedItems.customQueries = this.deleteCustomQueries ? this.customQueries : [];
		selectedItems.scripts = this.deleteScripts ? this.scripts : [];
		this.args.onChangeSelectedItems(selectedItems);
	}

	@action
	changeDeleteCustomQueries(value: boolean): void {
		this.deleteCustomQueries = value;
		this.updateSelectedItems();
	}

	@action
	changeDeleteScripts(value: boolean): void {
		this.deleteScripts = value;
		this.updateSelectedItems();
	}
	<template>
		{{#if this.showContent}}
			<div class="row">
				<div class="board-unused-metrics">
					<div class="label">
						<strong>Unused metrics clean-up</strong>
						(recommended)
					</div>

					<p>
						We recommend to remove unused custom and calculated metrics, you're not
						using anywhere else:
					</p>

					{{#if this.customQueriesCount}}
						<div>
							<FormCheckboxComponent
								@name="delete_custom_queries"
								@selected={{this.deleteCustomQueries}}
								@label={{this.customQueriesLabel}}
								@theme="red"
								@onChange={{this.changeDeleteCustomQueries}}
							/>
						</div>
					{{/if}}

					{{#if this.scriptsCount}}
						<div>
							<FormCheckboxComponent
								@name="delete_scripts"
								@selected={{this.deleteScripts}}
								@label={{this.scriptsLabel}}
								@theme="red"
								@onChange={{this.changeDeleteScripts}}
							/>
						</div>
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
